import { useRef } from "react";
import {
  CopyIcon,
  Loader2Icon,
  MoreHorizontalIcon,
  PencilIcon,
  Trash2Icon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";

export type MatrixActionsMenuProps = {
  name: string;
  onRename: () => void;
  onDuplicate: () => void;
  onRequestDelete: () => void;
  isDuplicating: boolean;
  align?: "start" | "end";
  triggerClassName?: string;
};

// Rename, duplicate and delete, behind three dots wherever a matrix is named.
export function MatrixActionsMenu({
  name,
  onRename,
  onDuplicate,
  onRequestDelete,
  isDuplicating,
  align = "start",
  triggerClassName,
}: MatrixActionsMenuProps) {
  const openedByPointerRef = useRef(false);

  return (
    // Not modal: inside the switcher's popover, a modal menu would take the popover down with it.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Matrix actions for ${name}`}
          title="Rename, duplicate or delete"
          onPointerDown={() => (openedByPointerRef.current = true)}
          onKeyDown={() => (openedByPointerRef.current = false)}
          className={cn(
            "text-muted-foreground hover:text-primary data-[state=open]:bg-foreground/8 data-[state=open]:text-foreground size-6 cursor-pointer",
            triggerClassName,
          )}
        >
          {isDuplicating ? (
            <Loader2Icon className="size-3.5 animate-spin" />
          ) : (
            <MoreHorizontalIcon className="size-3.5" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        // Radix returns focus to the trigger, which leaves a ring after a plain click.
        onCloseAutoFocus={(event) => {
          if (openedByPointerRef.current) event.preventDefault();
        }}
      >
        <DropdownMenuItem onClick={onRename}>
          <PencilIcon />
          Rename matrix
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDuplicate} disabled={isDuplicating}>
          <CopyIcon />
          Duplicate matrix
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={onRequestDelete}>
          <Trash2Icon />
          Delete matrix
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
